import React from "react";

/**
 * Tiny dependency-free donut chart. Renders 4 arcs (men/ladies/unisex/kids)
 * sized by their share of the total. When the total is 0 we draw a single
 * grey ring so the admin doesn't see an empty box.
 */
const COLORS = {
  ladies: "#D48CA6",   // soft rose
  men: "#4C6FA1",      // muted indigo
  unisex: "#B0876A",   // copper
  kids: "#F0B45B",     // amber
};

const LABELS = {
  ladies: "Ladies",
  men: "Men",
  unisex: "Unisex",
  kids: "Kids",
};

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const arcPath = (cx, cy, rOuter, rInner, start, end) => {
  const [x1, y1] = polar(cx, cy, rOuter, start);
  const [x2, y2] = polar(cx, cy, rOuter, end);
  const [x3, y3] = polar(cx, cy, rInner, end);
  const [x4, y4] = polar(cx, cy, rInner, start);
  const large = end - start > 180 ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
};

export default function GenderDonut({ title, data, testId }) {
  const keys = ["ladies", "men", "unisex", "kids"];
  const total = keys.reduce((sum, k) => sum + (data?.[k] || 0), 0);
  const cx = 90;
  const cy = 90;
  const rOuter = 78;
  const rInner = 48;

  let cursor = 0;
  const arcs = total > 0
    ? keys
        .filter((k) => (data?.[k] || 0) > 0)
        .map((k) => {
          const value = data[k];
          const share = value / total;
          const start = cursor * 360;
          cursor += share;
          const end = cursor * 360;
          return { key: k, value, start, end, share };
        })
    : [];

  return (
    <div className="gender-donut" data-testid={testId}>
      <h3 className="gender-donut-title">{title}</h3>
      <div className="gender-donut-body">
        <svg viewBox="0 0 180 180" width="180" height="180" aria-label={`${title} chart`}>
          {total === 0 ? (
            <circle
              cx={cx}
              cy={cy}
              r={(rOuter + rInner) / 2}
              fill="none"
              stroke="rgba(139,90,43,0.15)"
              strokeWidth={rOuter - rInner}
            />
          ) : (
            arcs.map((a) => (
              <path
                key={a.key}
                d={arcPath(cx, cy, rOuter, rInner, a.start, a.end)}
                fill={COLORS[a.key]}
                data-testid={`donut-slice-${a.key}`}
              >
                <title>{`${LABELS[a.key]}: ${a.value} (${Math.round(a.share * 100)}%)`}</title>
              </path>
            ))
          )}
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            className="gender-donut-center-num"
            data-testid={`donut-total-${testId || "chart"}`}
          >
            {total}
          </text>
          <text x={cx} y={cy + 16} textAnchor="middle" className="gender-donut-center-lbl">
            total
          </text>
        </svg>

        <ul className="gender-donut-legend">
          {keys.map((k) => {
            const v = data?.[k] || 0;
            const pct = total > 0 ? Math.round((v / total) * 100) : 0;
            return (
              <li key={k} data-testid={`donut-legend-${k}`}>
                <span className="dot" style={{ background: COLORS[k] }} />
                <span className="lbl">{LABELS[k]}</span>
                <span className="val">
                  {v}
                  {total > 0 ? ` · ${pct}%` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
